// NOVO ARQUIVO (módulo Nota de Venda/Remessa avulsa sem JOB — a pedido do usuário).
//
// Este service é uma ADAPTAÇÃO do fluxo de montagem/emissão de NF-e já existente em
// `src/views/IssuanceInvoice/IssuanceInvoiceView.vue` (métodos `createObjectEmissao`,
// `emitirPreviewNotaFiscal`, `previeNf` e helpers `getCodigoTributario`/`removerAcentos`/
// `normalizeCodigoCidade`/`mapTipoFreteToModalidadeFrete`/`getMeioPagamento`/`getSigla`).
//
// DECISÃO DE ARQUITETURA (registrada a pedido do usuário, que pediu um módulo separado
// "para não afetar nada do que faz hoje"): a lógica foi DUPLICADA aqui, em vez de extraída
// para um service compartilhado com `IssuanceInvoiceView.vue` — isso evita qualquer risco de
// regressão no fluxo de emissão vinculado a Job/Romaneio, que já está em produção. O ponto
// que ali dependia obrigatoriamente do Job (linha "informacoesComplementares: 'NOSSO JOB ' +
// nfInfo.notaFiscal[0].project[0].code + ...", que quebra com TypeError se não houver Job) foi
// substituído aqui por um texto fixo ("NOTA AVULSA - SEM JOB") quando não há Job vinculado.
//
// Igual ao fluxo original: a Pré-Visualização usa o endpoint dedicado `POST {baseUrl}/nfe/preview`
// (payload único, sem array) — NÃO transmite/emite a nota de verdade. A emissão real usa
// `POST {baseUrl}/nfe` (payload como array `[nfData]`) e está implementada aqui por completude,
// mas não deve ser chamada sem confirmação explícita do usuário.

import axios from "axios";
import moment from "moment";
import Vue from "vue";

const baseUrl = process.env.VUE_APP_PLUGNOTAS_API_URL || "https://api.plugnotas.com.br";
const apiKey = process.env.VUE_APP_PLUGNOTAS_API_X_API_KEY || "5bbf2aa2efee08a50be2cefaf8ab8811";

function getCodigoTributario(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  const texto = String(value).trim();
  if (!texto.length) return fallback;
  const codigo = texto.split(" - ")[0].trim();
  return codigo || fallback;
}

function normalizeCodigoCidade(value, descricaoCidade = "") {
  const codigo = String(value || "").replace(/\D/g, "");
  const cidade = String(descricaoCidade || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase();

  if (codigo === "352940" && cidade === "MAUA") return "3529401";
  return codigo;
}

function removerAcentos(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getSigla(estado) {
  switch (estado) {
    case "Acre": return "AC";
    case "Alagoas": return "AL";
    case "Amapá": return "AP";
    case "Amazonas": return "AM";
    case "Bahia": return "BA";
    case "Ceará": return "CE";
    case "Distrito Federal": return "DF";
    case "Espírito Santo": return "ES";
    case "Goiás": return "GO";
    case "Maranhão": return "MA";
    case "Mato Grosso": return "MT";
    case "Mato Grosso do Sul": return "MS";
    case "Minas Gerais": return "MG";
    case "Pará": return "PA";
    case "Paraíba": return "PB";
    case "Paraná": return "PR";
    case "Pernambuco": return "PE";
    case "Piauí": return "PI";
    case "Rio de Janeiro": return "RJ";
    case "Rio Grande do Norte": return "RN";
    case "Rio Grande do Sul": return "RS";
    case "Rondônia": return "RO";
    case "Roraima": return "RR";
    case "Santa Catarina": return "SC";
    case "São Paulo": return "SP";
    case "Sergipe": return "SE";
    case "Tocantins": return "TO";
    default: return null;
  }
}

function mapTipoFreteToModalidadeFrete(tipoFrete) {
  if (!tipoFrete) return 9; // "Sem transporte" — avulsa pode não ter transportadora

  const codigo = tipoFrete.trim().substring(0, 3).toUpperCase();

  // BUGFIX (mesmo ajuste replicado de IssuanceInvoiceView.vue::mapTipoFreteToModalidadeFrete):
  // TCR (Transporte Próprio) e SOT (Sem Ocorrência de Transporte) não estavam nas listas abaixo e
  // caíam no fallback genérico "9" — errado para TCR, que corresponde ao código SEFAZ 3 (Transporte
  // Próprio por conta do Remetente), não 9 (Sem frete).
  if (codigo === 'TCR') return 3;
  if (codigo === 'SOT') return 9;

  const vendedorPaga = ["CIF", "CFR", "CIP", "CPT", "DAP", "DPU", "DDP"];
  const compradorPaga = ["FOB", "EXW", "FCA", "FAS"];

  if (vendedorPaga.includes(codigo)) return 0;
  if (compradorPaga.includes(codigo)) return 1;
  return 9;
}

function getMeioPagamento(meioPagamento) {
  switch (meioPagamento) {
    case "Dinheiro": return "01";
    case "Cheque": return "02";
    case "Cartão de Crédito": return "03";
    case "Cartão de Débito": return "04";
    case "Cartão da Loja": return "05";
    case "Vale Alimentação": return "10";
    case "Vale Refeição": return "11";
    case "Vale Presente": return "12";
    case "Vale Combustível": return "13";
    case "Boleto Bancário": return "15";
    case "Depósito Bancário": return "16";
    case "Pagamento Instantâneo (PIX) - Dinâmico": return "17";
    case "Transferência bancária, Carteira Digital": return "18";
    case "Programa de fidelidade, Cashback, Crédito Virtual": return "19";
    case "Pagamento Instantâneo (PIX) - Estático": return "20";
    case "Crédito em Loja": return "21";
    case "Pagamento Eletrônico não Informado - falha de hardware do sistema emissor": return "22";
    case "Sem pagamento": return "90";
    case "Pagamento Posterior": return "99";
    default: return "99";
  }
}

/**
 * Monta o payload de emissão de NF-e (Venda ou Remessa) a partir de um registro
 * FisPreDocEmi (com `itens`, `parcelas` etc. já persistidos) — igual ao createObjectEmissao
 * original, mas sem exigir Job.
 *
 * @param {Object} fisPreDocEmi - registro FisPreDocEmi já salvo (com id).
 * @param {Object} contexto - dados adicionais: { destinatarioNome, destinatarioFornecedorNome,
 *   notaFiscalDeRemessa, tipoNotaFiscal, destinatarioInfo, destinatarioInfoFornecedor, email }
 */
async function montarPayloadNFe(fisPreDocEmi, contexto = {}) {
  const nfInfo = {
    notaFiscal: [fisPreDocEmi],
    notaFiscalDeRemessa: !!contexto.notaFiscalDeRemessa,
    tipoNotaFiscal: contexto.tipoNotaFiscal,
    destinatarioNome: contexto.destinatarioNome,
    destinatarioFornecedorNome: contexto.destinatarioFornecedorNome,
    destinatarioInfo: contexto.destinatarioInfo || [],
    destinatarioInfoFornecedor: contexto.destinatarioInfoFornecedor || [],
    email: contexto.email
  };

  let parcelas = fisPreDocEmi?.parcelas;
  let fisPreDocEmiItem = fisPreDocEmi?.itens;
  let itens = [];
  let sum = 0;

  let fisPreDocEmiFin = [];
  let parcelasFormatadas = [];
  let valorLiquido = 0;
  let totalCobranca = 0;

  // BUGFIX (achado no teste da Nota de Venda Sem JOB): alguns registros do catálogo de NCM
  // agrupam duas variações do código no mesmo campo "codigo" (ex.: item selecionado no formulário
  // trouxe "8414.59.10 8414.59.90" — ambas descritas como "Motoventiladores" no cadastro, já que
  // é o mesmo produto sob duas classificações fiscais possíveis). Sem tratar isso, remover só os
  // pontos concatenava os dois códigos num NCM de 16 dígitos ("8414591084145990"), que o PlugNotas
  // rejeita na prévia com "formato aceito: 2 dígitos numéricos (99) ou 8 dígitos numéricos
  // (99999999)". Agora usamos apenas o primeiro código do grupo (primeiro token separado por
  // espaço) antes de remover os não-dígitos — mantém CEST (que nunca vem com espaço) inalterado.
  const normalizePlugNotasNumericField = (value, fallback = "") => {
    const primeiroToken = (v) => String(v ?? "").trim().split(/\s+/)[0] || "";
    const normalized = primeiroToken(value ?? fallback).replace(/\D/g, "");
    return normalized || primeiroToken(fallback).replace(/\D/g, "");
  };

  if (parcelas?.length > 0 && !nfInfo.notaFiscalDeRemessa) {
    const parcelasResponse = await Vue.prototype.$http.get("/Entity/FisPreDocEmiFin/search-fields", {
      params: { ids: parcelas.map((id) => id).join(","), sortField: "parcelaSequencia" }
    });
    fisPreDocEmiFin = parcelasResponse.data.data.data;
    parcelasFormatadas = fisPreDocEmiFin.reduce((acc, p) => {
      acc.push({
        dataVencimento: new Date(p.dtPag),
        numero: `00${p.parcelaSequencia}`,
        tipoPagamento: 1,
        valor: p.vlr / 100
      });
      valorLiquido += p.vlr / 100;
      totalCobranca += p.vlr / 100;
      return acc;
    }, []);
  }

  const cfopNota = String(fisPreDocEmi?.cfop || "").trim();
  const exigirIcmsRemessa = nfInfo.notaFiscalDeRemessa && ["5901", "6901"].includes(cfopNota);

  let transportadora;
  if (fisPreDocEmi?.transportadora) {
    const response = await Vue.prototype.$http.get(`/Entity/Fornecedor/${fisPreDocEmi.transportadora}`);
    transportadora = response.data.data;
  }

  if (fisPreDocEmiItem?.length > 0) {
    await Vue.prototype.$http.get("/Entity/FisPreDocEmiItem/search-fields", {
      params: { ids: fisPreDocEmiItem.map((id) => id).join(","), sortField: "sequencia" }
    }).then((response) => {
      response.data.data.data.map((item) => {
        sum += item.valorTotal;

        const cstIcms = getCodigoTributario(item.ctsIcms, "00");
        const cstPis = getCodigoTributario(item.ctsPis, "01");
        const cstCofins = getCodigoTributario(item.ctsCofins, "01");
        const icmsSemTributacao = ["40", "41", "50"].includes(cstIcms);
        const pisIsento = cstPis === "07";
        const cofinsIsento = cstCofins === "07";
        const origemIcms = getCodigoTributario(item.icmsOrigem || item.origemMercadoria || item.origem, "0");
        const percentualReducaoIcms = (() => {
          const rawValue = item.icmsPercentualBaseCalculo;
          if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") return undefined;
          const normalizado = String(rawValue).trim().replace(/\./g, "").replace(",", ".");
          const parsed = Number(normalizado);
          return Number.isFinite(parsed) ? parsed : undefined;
        })();

        let descricaoCortada = String(item?.descricao || "").substring(0, 120);
        const ultimoEspaco = descricaoCortada.lastIndexOf(" ");
        if (ultimoEspaco > 0) descricaoCortada = descricaoCortada.substring(0, ultimoEspaco);
        const complemento = String(item?.descricao || "").substring(descricaoCortada.length).trim();

        const ehVenda = !nfInfo.notaFiscalDeRemessa;
        const valorBaseCalculoIcms = ehVenda
          ? (item.icmsBaseCalculo != null && item.icmsBaseCalculo !== "" ? item.icmsBaseCalculo / 100
            : (item.resultBaseCalcICMS != null && item.resultBaseCalcICMS !== "" ? item.resultBaseCalcICMS / 100 : undefined))
          : (item.resultBaseCalcICMS != null && item.resultBaseCalcICMS !== "" ? item.resultBaseCalcICMS / 100
            : (item.icmsBaseCalculo != null && item.icmsBaseCalculo !== "" ? item.icmsBaseCalculo / 100 : undefined));
        const aliquotaIcms = Number(String(item.icmsPercentual ?? 0).replaceAll(",", "."));
        const enviarAliquotaIcms = !nfInfo.notaFiscalDeRemessa || exigirIcmsRemessa;
        const enviarBaseCalculoIcms = cstIcms !== "50" && cstIcms !== "40"
          && (icmsSemTributacao || nfInfo?.tipoNotaFiscal === "Nota_Fiscal" || exigirIcmsRemessa);
        const valorAliquotaIcms = cstIcms === "50" ? undefined
          : cstIcms === "40" ? undefined
          : icmsSemTributacao ? 0
          : (enviarAliquotaIcms ? aliquotaIcms : undefined);

        const pedidoCompraItem = String(item?.sequenciaPedidoDeCompra ?? "").trim();
        const numeroCompraItem = String(item?.pedidodecompra ?? "").trim();
        const ncmItem = normalizePlugNotasNumericField(item?.ncm, fisPreDocEmi?.ncm);
        const cestItem = normalizePlugNotasNumericField(item?.cest, fisPreDocEmi?.cest);

        itens.push({
          codigo: item.codigo,
          descricao: removerAcentos(descricaoCortada),
          ncm: ncmItem || undefined,
          cest: cestItem || undefined,
          cfop: fisPreDocEmi?.cfop || "5101",
          valorFrete: (item?.valorFrete || 0) / 100,
          valorDesconto: (item?.valorDesconto || 0) / 100,
          valorOutros: (item?.valorOutros || 0) / 100,
          valorSeguro: (item?.valorSeguro || 0) / 100,
          informacoesComplementares: `${complemento} ${item?.observacao || ""}`.replace(/\n/g, "").trim() || undefined,
          ...(pedidoCompraItem ? { pedidoCompra: pedidoCompraItem } : {}),
          ...(numeroCompraItem ? { numeroCompra: numeroCompraItem } : {}),
          unidade: {
            comercial: item?.unidadeSigla || "PC",
            tributavel: item?.unidadeSigla || "PC"
          },
          valorUnitario: {
            comercial: item.valorUnitario / 100,
            tributavel: item.valorUnitario / 100
          },
          quantidade: {
            comercial: Number(String(item.quantidade).replaceAll(",", ".")),
            tributavel: Number(String(item.quantidade).replaceAll(",", "."))
          },
          valor: item.valorTotal / 100,
          codigoBeneficioFiscal: fisPreDocEmi?.codigoBeneficioFiscal || undefined,
          tributos: {
            icms: {
              origem: origemIcms,
              cst: cstIcms,
              baseCalculo: enviarBaseCalculoIcms ? {
                modalidadeDeterminacao: 0,
                valor: icmsSemTributacao ? 0 : valorBaseCalculoIcms,
                percentualReducao: cstIcms === "20" ? (percentualReducaoIcms ?? 0) : undefined
              } : undefined,
              aliquota: valorAliquotaIcms,
              valor: (item.icmsValor && (nfInfo?.tipoNotaFiscal === "Nota_Fiscal" || nfInfo.notaFiscalDeRemessa))
                ? item.icmsValor / 100 : undefined,
              substituicaoTributaria: cstIcms == "10" || cstIcms == "70" ? {
                aliquota: Number(item.aliqDestinoICMS?.replaceAll(",", ".")) || undefined,
                baseCalculo: {
                  modalidadeDeterminacao: Number(item.modalidadeDeterminacao?.split(" - ")[0]) || 0,
                  valor: item.resultBaseCalcICMS / 100 || undefined
                }
              } : undefined
            },
            pis: {
              cst: cstPis,
              baseCalculo: { valor: pisIsento ? 0 : (item.pisBaseCalculo || 0) / 100, quantidade: 0 },
              aliquota: pisIsento ? 0 : Number(String(item.pisPercentual ?? 0).replaceAll(",", ".")) || 0,
              valor: pisIsento ? 0 : Number(item.pisValor || 0) / 100
            },
            cofins: {
              cst: cstCofins,
              baseCalculo: { valor: cofinsIsento ? 0 : (item.cofinsBaseCalculo || 0) / 100 },
              aliquota: cofinsIsento ? 0 : Number(String(item.cofinsPercentual ?? 0).replaceAll(",", ".")) || 0,
              valor: cofinsIsento ? 0 : (item.cofinsValor || 0) / 100
            },
            ipi: {
              cst: item?.ctsIpi?.split(" - ")[0] || "50",
              baseCalculo: (item.ipiBaseCalculo || 0) / 100,
              valor: (item.ipiValor || 0) / 100,
              aliquota: Number(item?.ipiPercentual) || 0,
              codigoEnquadramentoLegal: "103"
            }
          }
        });
      });
    });
  }

  const destinatarioInfoBase = nfInfo.notaFiscalDeRemessa
    ? nfInfo?.destinatarioInfoFornecedor?.[0]
    : nfInfo?.destinatarioInfo?.[0];

  const telefoneDestinatario = destinatarioInfoBase?.contatos?.[0]?.telefone?.replace(/\D/g, "");

  const dataBaseEmissao = moment(fisPreDocEmi?.dataEmissao || fisPreDocEmi?.createAt || new Date())
    .utcOffset(-3)
    .format("YYYY-MM-DDT00:00:00-03:00");

  // Só existe JOB no fluxo tradicional (vinculado); nota avulsa não tem — usa texto fixo.
  const infoComplementarBase = fisPreDocEmi?.job
    ? `NOSSO JOB ${fisPreDocEmi?.jobCodigo || fisPreDocEmi.job}`
    : "NOTA AVULSA - SEM JOB (emitida fora do fluxo de Job/Ordem de Serviço)";

  const nfData = {
    idIntegracao: `XXXYY${Math.floor(100000 + Math.random() * 900000)}`,
    dataEmissao: dataBaseEmissao,
    presencial: "1",
    consumidorFinal: true,
    informacoesComplementares: `${infoComplementarBase}${(fisPreDocEmi?.informacaoAdd || "").replace(/\n/g, "")}`,
    natureza: fisPreDocEmi?.naturezaOperacao || "OPERAÇÃO INTERNA",
    notaReferenciada: fisPreDocEmi?.notaFiscalMae ? { nfe: [{ chave: fisPreDocEmi.notaFiscalMae }] } : undefined,
    emitente: {
      cpfCnpj: (fisPreDocEmi?.cnpjFilial || "").replace(/\D/g, "") || "02823203000103",
      inscricaoEstadual: "540036650111",
      telefone: { dd: "11", numero: "32186666" }
    },
    destinatario: {
      cpfCnpj: (fisPreDocEmi?.cnpj || "").replace(/\D/g, ""),
      razaoSocial: !nfInfo.notaFiscalDeRemessa ? nfInfo?.destinatarioNome : nfInfo?.destinatarioFornecedorNome,
      email: nfInfo?.email || "contato@cliente.com.br",
      inscricaoEstadual: destinatarioInfoBase?.inscricaoEstadual?.replace(/\D/g, "") || undefined,
      telefone: {
        dd: telefoneDestinatario ? telefoneDestinatario.slice(0, 2) : "11",
        numero: telefoneDestinatario ? telefoneDestinatario.slice(2) : "999999999"
      },
      endereco: {
        logradouro: fisPreDocEmi?.destinatarioLogradouro || destinatarioInfoBase?.cobrancaEndereco || "Logradouro não informado",
        numero: fisPreDocEmi?.destinatarioNumero || destinatarioInfoBase?.cobrancaNumero || "S/N",
        bairro: fisPreDocEmi?.destinatarioBairro || destinatarioInfoBase?.cobrancaBairro || "Bairro não informado",
        codigoCidade: normalizeCodigoCidade(
          fisPreDocEmi?.codigoCidade || "4115200",
          fisPreDocEmi?.destinatarioCidade || destinatarioInfoBase?.cobrancaCidadeNome
        ),
        descricaoCidade: fisPreDocEmi?.destinatarioCidade || destinatarioInfoBase?.cobrancaCidadeNome || "Cidade não informada",
        estado: fisPreDocEmi?.uf || "MG",
        cep: (fisPreDocEmi?.destinatarioCep || destinatarioInfoBase?.cobrancaCEP || "").replace(/\D/g, "") || "00000000"
      }
    },
    itens,
    pagamentos: [
      {
        aVista: fisPreDocEmi?.condicaoPagamento == "á vista",
        // BUGFIX (achado durante o teste): o PlugNotas só aceita o código como STRING de 2
        // dígitos (ex.: "01"), nunca o número 1 — o fallback anterior enviava o inteiro 1 quando
        // meioPagamento vinha vazio, causando rejeição ("Valor inválido para o campo pagamentos[0].meio").
        meio: fisPreDocEmi?.meioPagamento?.replaceAll("_", " ")
          ? getMeioPagamento(fisPreDocEmi?.meioPagamento?.replaceAll("_", " ")) || "01"
          : "01",
        valor: sum / 100
      }
    ],
    cobranca: fisPreDocEmi?.condicaoPagamento == "á vista" ? undefined : {
      numero: "12345",
      valorLiquido: fisPreDocEmiFin.length > 0 ? valorLiquido : sum / 100,
      valorTotal: fisPreDocEmiFin.length > 0 ? totalCobranca : sum / 100,
      parcelas: fisPreDocEmiFin.length > 0 ? parcelasFormatadas : undefined
    },
    transporte: {
      modalidadeFrete: mapTipoFreteToModalidadeFrete(fisPreDocEmi?.transporteTipo).toString(),
      transportador: fisPreDocEmi?.transportadora ? {
        cnpj: transportadora?.cnpj?.replace(/\D/g, ""),
        inscricaoEstadual: transportadora?.inscricaoEstadual?.replace(/\D/g, "") || undefined,
        nome: transportadora?.nomeFantasia || transportadora?.razaoSocial,
        endereco: {
          logradouro: transportadora?.entregaEndereco,
          descricaoCidade: transportadora?.entregaCidadeNome,
          uf: getSigla(transportadora?.entregaEstadoNome)
        }
      } : undefined,
      volumes: fisPreDocEmi?.volumeQuantidade ? [
        {
          quantidade: Number(fisPreDocEmi.volumeQuantidade),
          especie: fisPreDocEmi.especie,
          marca: fisPreDocEmi.marca,
          numeracao: fisPreDocEmi.volumeNumeracao,
          pesoLiquido: Number(String(fisPreDocEmi?.pesoLiquido || "0").replace(",", ".")),
          pesoBruto: Number(String(fisPreDocEmi?.pesoBruto || "0").replace(",", "."))
        }
      ] : undefined
    },
    dataSaidaEntrada: dataBaseEmissao
  };

  return nfData;
}

/**
 * Pré-visualização: monta o payload e chama SOMENTE o endpoint de preview do PlugNotas
 * (`POST /nfe/preview`), que NUNCA transmite/emite a nota de verdade. Retorna
 * { protocol, pdfBase64 } (o PDF só é buscado se um protocolo válido for retornado).
 */
async function previewNf(fisPreDocEmi, contexto = {}, onProgress = () => {}) {
  const nfData = await montarPayloadNFe(fisPreDocEmi, contexto);

  let previewResponse;
  try {
    const response = await axios.post(`${baseUrl}/nfe/preview`, nfData, {
      headers: { "X-API-KEY": apiKey, "Content-Type": "application/json" }
    });
    previewResponse = response.data;
  } catch (error) {
    const apiError = error?.response?.data;
    if (apiError?.error?.message === "Falha na validação do JSON de Prévia da DANFe") {
      const fields = apiError.error.data?.fields || {};
      const detailList = Object.entries(fields).map(([field, msg]) => `${field}: ${msg}`);
      return { erro: true, tituloErro: "Falha na validação da prévia da DANFe", mensagens: detailList, nfData };
    }
    return { erro: true, tituloErro: "Erro ao gerar prévia", mensagens: [apiError?.message || error.message], nfData };
  }

  if (!previewResponse?.protocol) {
    return { erro: true, tituloErro: "PlugNotas não retornou protocolo de prévia", mensagens: [], nfData, respostaBruta: previewResponse };
  }

  const maxTentativas = 6;
  const esperaEntreTentativasMs = 5000;
  let response = null;
  let ultimoErro = null;

  for (let tentativa = 1; tentativa <= maxTentativas; tentativa++) {
    await new Promise((r) => setTimeout(r, esperaEntreTentativasMs));
    onProgress(tentativa, maxTentativas);
    try {
      response = await axios.get(`https://api.plugnotas.com.br/nfe/${previewResponse.protocol}/preview`, {
        responseType: "arraybuffer",
        responseEncoding: "binary",
        ContentType: "application/pdf",
        headers: { "Content-Type": "application/json", "x-api-key": "5bbf2aa2efee08a50be2cefaf8ab8811" }
      });
      ultimoErro = null;
      break;
    } catch (error) {
      ultimoErro = error;
    }
  }

  if (!response) {
    return {
      erro: true,
      tituloErro: "Não foi possível carregar a prévia da DANFe",
      mensagens: [ultimoErro?.message || "tempo esgotado"],
      nfData,
      protocol: previewResponse.protocol
    };
  }

  const buffer = Buffer.from(response.data);
  const base64Pdf = buffer.toString("base64");

  return { erro: false, protocol: previewResponse.protocol, pdfBase64: base64Pdf, nfData };
}

/**
 * Emissão REAL (transmite a nota para a Receita via PlugNotas). Implementada por completude/
 * paridade com o fluxo original, mas NÃO deve ser chamada sem confirmação explícita do usuário —
 * o teste deste módulo (a pedido do usuário) vai somente até `previewNf` acima.
 */
async function emitirNf(fisPreDocEmi, contexto = {}) {
  const nfData = await montarPayloadNFe(fisPreDocEmi, contexto);
  const response = await axios.post(`${baseUrl}/nfe`, [nfData], {
    headers: { "X-API-KEY": apiKey, "Content-Type": "application/json" }
  });
  return response.data;
}

export default {
  montarPayloadNFe,
  previewNf,
  emitirNf,
  getCodigoTributario,
  normalizeCodigoCidade,
  removerAcentos,
  getSigla,
  mapTipoFreteToModalidadeFrete,
  getMeioPagamento
};
